import React, { useEffect, useRef } from 'react';
import { supabase } from '../integrations/client.ts';

/**
 * Verified - Email verification success page
 *
 * This page is shown after the user's email has been successfully verified.
 * If coming from email verification link (with code param), we:
 * 1. Listen for Supabase auth state change (when code is exchanged for session)
 * 2. Extract user info from the session
 * 3. Send welcome email asynchronously
 * 4. Clear the auto-created session
 * They can choose to sign in or close the page.
 */
function Verified({ onNavigateToSignIn, onNavigateToLanding }) {
    const hasRunRef = useRef(false);
    const emailSentRef = useRef(false);

    useEffect(() => {
        // Prevent running twice
        if (hasRunRef.current) return;
        hasRunRef.current = true;

        const handleVerificationFlow = async () => {
            // Check if this is from email verification redirect (PKCE flow uses ?code= parameter)
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');

            if (code) {
                // Listen for auth state change (when Supabase exchanges code for session)
                const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                    // Only process SIGNED_IN event (code was exchanged for session)
                    if (event === 'SIGNED_IN' && session && session.user && !emailSentRef.current) {
                        emailSentRef.current = true; // Prevent duplicate sends

                        const userId = session.user.id;
                        const email = session.user.email;
                        const name = session.user.user_metadata?.name || email?.split('@')[0] || 'User';

                        // Send welcome email and wait for it to complete before signing out
                        await sendWelcomeEmail(userId, email, name, session.access_token);

                        // Unsubscribe from auth changes
                        subscription.unsubscribe();

                        // Clear the code from URL
                        window.history.replaceState(null, '', window.location.pathname + '?view=verified');

                        // Wait a moment, then sign out to clear the auto-created session
                        setTimeout(async () => {
                            await supabase.auth.signOut();

                            // Only clear the specific auth token key used by this client
                            // This prevents clearing other important Supabase data or sessions from other tabs
                            const authStorageKey = 'supabase.auth.token';
                            if (localStorage.getItem(authStorageKey)) {
                                localStorage.removeItem(authStorageKey);
                            }
                        }, 1000);
                    }
                });

                // Cleanup function
                return () => {
                    subscription.unsubscribe();
                };
            }
        };

        handleVerificationFlow();
    }, []);

    // Async function to send welcome email (happens in background)
    const sendWelcomeEmail = async (userId, email, name, accessToken) => {
        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

            const response = await fetch(`${supabaseUrl}/functions/v1/send-welcome-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    userId,
                    email,
                    name
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Failed to send welcome email:', errorData);
            }
        } catch (emailError) {
            console.error('Error sending welcome email:', emailError);
            // Don't fail the verification flow if email fails
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#E2DFD2',
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#F5F3ED',
                borderRadius: '16px',
                padding: '48px',
                maxWidth: '500px',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.03)'
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: '#E2DFD2',
                    borderRadius: '50%',
                    margin: '0 auto 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    color: '#333'
                }}>
                    ✓
                </div>
                <h2 style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '12px'
                }}>
                    Email Verified!
                </h2>
                <p style={{
                    fontSize: '16px',
                    color: '#666',
                    marginBottom: '32px'
                }}>
                    Your account has been verified successfully. You can now sign in to access your account.
                </p>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }}>
                    <button
                        onClick={onNavigateToSignIn}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#E2DFD2',
                            backgroundColor: '#333',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                        Sign In
                    </button>
                    <button
                        onClick={onNavigateToLanding}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            fontWeight: '500',
                            color: '#333',
                            backgroundColor: 'transparent',
                            border: '2px solid #333',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#E2DFD2';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Verified;
